/* eslint-disable @typescript-eslint/no-empty-function */
/**
 * スキルマスター管理ページ（SYSTEM_ADMIN only）
 *
 * スキルマスター一覧の閲覧・検索・インライン編集・カテゴリ変更・
 * アイコンアップロード/削除を行う管理画面。
 */
import { ChevronLeft, Pencil, Trash2, Upload } from "lucide-react";
import {
  type ChangeEvent,
  type FC,
  type KeyboardEvent,
  useCallback,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { PageTitle } from "../../../../components/PageTitle";
import {
  type AdminSkill,
  type UseAdminSkillsApiOptions,
  useAdminSkillsApi,
  useDeleteSkillIconApi,
  useUpdateAdminSkillApi,
  useUploadSkillIconApi,
} from "../../../../hooks/useAdminApi";

// =====================================
// Constants
// =====================================

const PAGE_SIZE = 50;

const CATEGORY_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "未分類" },
  { value: "language", label: "言語" },
  { value: "framework", label: "FW" },
  { value: "cloud", label: "クラウド" },
  { value: "database", label: "DB" },
  { value: "infrastructure", label: "インフラ" },
  { value: "ai", label: "AI" },
  { value: "tool", label: "ツール" },
  { value: "os", label: "OS" },
  { value: "other", label: "その他" },
];

const FILTER_CATEGORY_OPTIONS = [
  { value: "", label: "全カテゴリ" },
  { value: "null", label: "未分類" },
  ...CATEGORY_OPTIONS.filter(o => o.value !== ""),
];

// =====================================
// Component
// =====================================

/**
 * スキルマスター管理ページ
 * @return {JSX.Element} 管理ページ
 */
export const SkillMastersPage: FC = () => {
  // Filters & pagination
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [currentPage, setCurrentPage] = useState(0);

  const apiOptions: UseAdminSkillsApiOptions = {
    q: searchQuery || undefined,
    category: filterCategory || undefined,
    offset: currentPage * PAGE_SIZE,
    limit: PAGE_SIZE,
  };

  const { data, loading, refetch } = useAdminSkillsApi(apiOptions);
  const { updateSkill } = useUpdateAdminSkillApi();
  const { uploadIcon } = useUploadSkillIconApi();
  const { deleteIcon } = useDeleteSkillIconApi();

  // Inline edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");

  // File input ref
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadTargetId, setUploadTargetId] = useState<string | null>(null);

  // Search with debounce
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const handleSearchChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchQuery(value);
      setCurrentPage(0);
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => {
        refetch({
          q: value || undefined,
          category: filterCategory || undefined,
          offset: 0,
          limit: PAGE_SIZE,
        }).catch(() => {});
      }, 300);
    },
    [filterCategory, refetch],
  );

  const handleCategoryFilterChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value;
      setFilterCategory(value);
      setCurrentPage(0);
      refetch({
        q: searchQuery || undefined,
        category: value || undefined,
        offset: 0,
        limit: PAGE_SIZE,
      }).catch(() => {});
    },
    [searchQuery, refetch],
  );

  // Inline display_name edit
  const handleStartEdit = useCallback((skill: AdminSkill) => {
    setEditingId(skill.id);
    setEditValue(skill.displayName);
  }, []);

  const handleSaveEdit = useCallback(
    async (id: string) => {
      if (!editValue.trim()) return;
      try {
        await updateSkill(id, { displayName: editValue.trim() });
        setEditingId(null);
        refetch().catch(() => {});
      } catch {
        // error handled by hook
      }
    },
    [editValue, updateSkill, refetch],
  );

  const handleEditKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>, id: string) => {
      if (e.key === "Enter") {
        handleSaveEdit(id);
      } else if (e.key === "Escape") {
        setEditingId(null);
      }
    },
    [handleSaveEdit],
  );

  // Category change (instant save)
  const handleCategoryChange = useCallback(
    async (id: string, newCategory: string) => {
      try {
        await updateSkill(id, {
          category: newCategory || null,
        });
        refetch().catch(() => {});
      } catch {
        // error handled by hook
      }
    },
    [updateSkill, refetch],
  );

  // Icon upload
  const handleIconClick = useCallback((skillId: string) => {
    setUploadTargetId(skillId);
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !uploadTargetId) return;

      try {
        await uploadIcon(uploadTargetId, file);
        refetch().catch(() => {});
      } catch {
        // error handled by hook
      } finally {
        setUploadTargetId(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    },
    [uploadTargetId, uploadIcon, refetch],
  );

  // Icon delete
  const handleDeleteIcon = useCallback(
    async (skillId: string) => {
      try {
        await deleteIcon(skillId);
        refetch().catch(() => {});
      } catch {
        // error handled by hook
      }
    },
    [deleteIcon, refetch],
  );

  // Pagination
  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
      refetch({
        q: searchQuery || undefined,
        category: filterCategory || undefined,
        offset: page * PAGE_SIZE,
        limit: PAGE_SIZE,
      }).catch(() => {});
    },
    [searchQuery, filterCategory, refetch],
  );

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-4">
        <Link to="/admin" className="btn btn-ghost btn-sm gap-1 mb-2">
          <ChevronLeft className="w-4 h-4" />
          管理画面に戻る
        </Link>
        <PageTitle>スキルマスター管理</PageTitle>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-wrap gap-4 mb-4">
        <input
          type="text"
          value={searchQuery}
          onChange={handleSearchChange}
          className="input input-bordered input-sm w-64"
          placeholder="スキル名で検索..."
        />
        <select
          value={filterCategory}
          onChange={handleCategoryFilterChange}
          className="select select-bordered select-sm w-40"
        >
          {FILTER_CATEGORY_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <span className="text-sm text-base-content/60 self-center">
          全 {totalCount.toLocaleString()} 件
        </span>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/svg+xml,image/webp"
        className="hidden"
        onChange={handleFileChange}
      />

      {/* Table */}
      <div className="card bg-base-100 shadow-sm">
        <div className="overflow-x-auto">
          <table className="table table-sm">
            <thead>
              <tr>
                <th className="w-12">Icon</th>
                <th>表示名</th>
                <th>正規化名</th>
                <th className="w-36">カテゴリ</th>
                <th className="w-24">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading && !data ? (
                <tr>
                  <td colSpan={5} className="text-center py-8">
                    <span className="loading loading-spinner loading-md" />
                  </td>
                </tr>
              ) : data?.nodes.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="text-center py-8 text-base-content/50"
                  >
                    スキルが見つかりません
                  </td>
                </tr>
              ) : (
                data?.nodes.map(skill => (
                  <tr key={skill.id} className="hover">
                    {/* Icon */}
                    <td>
                      {skill.iconUrl ? (
                        <img
                          src={skill.iconUrl}
                          alt=""
                          className="w-6 h-6 object-contain"
                        />
                      ) : (
                        <span className="text-base-content/30 text-xs">-</span>
                      )}
                    </td>

                    {/* Display name (inline edit) */}
                    <td>
                      {editingId === skill.id ? (
                        <input
                          type="text"
                          value={editValue}
                          onChange={e => setEditValue(e.target.value)}
                          onKeyDown={e => handleEditKeyDown(e, skill.id)}
                          onBlur={() => handleSaveEdit(skill.id)}
                          className="input input-bordered input-xs w-48"
                          // biome-ignore lint/a11y/noAutofocus: intentional for inline edit UX
                          autoFocus
                        />
                      ) : (
                        <span className="flex items-center gap-1">
                          {skill.displayName}
                          <button
                            type="button"
                            className="btn btn-ghost btn-xs opacity-30 hover:opacity-100"
                            onClick={() => handleStartEdit(skill)}
                          >
                            <Pencil className="w-3 h-3" />
                          </button>
                        </span>
                      )}
                    </td>

                    {/* Normalized name (read-only) */}
                    <td className="text-base-content/60 font-mono text-xs">
                      {skill.normalizedName}
                    </td>

                    {/* Category select */}
                    <td>
                      <select
                        value={skill.category || ""}
                        onChange={e =>
                          handleCategoryChange(skill.id, e.target.value)
                        }
                        className="select select-bordered select-xs w-full"
                      >
                        {CATEGORY_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </td>

                    {/* Actions */}
                    <td>
                      <div className="flex gap-1">
                        <button
                          type="button"
                          className="btn btn-ghost btn-xs"
                          title="アイコンをアップロード"
                          onClick={() => handleIconClick(skill.id)}
                        >
                          <Upload className="w-3.5 h-3.5" />
                        </button>
                        {skill.iconUrl && (
                          <button
                            type="button"
                            className="btn btn-ghost btn-xs text-error"
                            title="アイコンを削除"
                            onClick={() => handleDeleteIcon(skill.id)}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-4">
          <button
            type="button"
            className="btn btn-sm"
            disabled={currentPage === 0}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            &lt;
          </button>
          <span className="text-sm">
            {currentPage + 1} / {totalPages}
          </span>
          <button
            type="button"
            className="btn btn-sm"
            disabled={currentPage >= totalPages - 1}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            &gt;
          </button>
        </div>
      )}
    </div>
  );
};
